/* LexiqWordmark — global brand wordmark used everywhere "LEXIQ" appears.
   Rule: LEX is always black/navy (or white on dark variant), IQ is always
   a vivid green AND visibly bolder.
   Variants:
   - default: LEX kحلي #0A192F (for light backgrounds)
   - light:   LEX أبيض #FFFFFF (for dark backgrounds — e.g., hero cards)
   - gold:    LEX ذهبي #E6C463 (for premium dark backgrounds)
   Sizes are controlled via the `className` prop (text-[..px]). */
import { forwardRef } from "react";

const VARIANT_LEX = {
  default: "text-[#0A192F]",
  light: "text-white",
  gold: "text-[#E6C463]",
};
const VARIANT_IQ = {
  default: "text-[#22C55E]",
  light: "text-[#22C55E]",
  gold: "text-[#22C55E]",
};

const LexiqWordmark = forwardRef(function LexiqWordmark(
  { className = "text-[20px]", testid = "lexiq-wordmark", variant = "default" },
  ref,
) {
  const lex = VARIANT_LEX[variant] || VARIANT_LEX.default;
  const iq = VARIANT_IQ[variant] || VARIANT_IQ.default;
  return (
    <span
      ref={ref}
      dir="ltr"
      data-testid={testid}
      className={`inline-flex items-baseline font-tajawal tracking-[0.02em] ${className}`}
    >
      <span className={`font-bold ${lex}`}>LEX</span>
      <span className={`font-extrabold ${iq}`}>IQ</span>
    </span>
  );
});

export default LexiqWordmark;

/* LexiqText — drop-in JSX helper that wraps every standalone occurrence of
   "LexIQ" / "LEXIQ" / "ليكس آي كيو" inside an arbitrary string with the
   styled wordmark, while preserving the rest of the text verbatim.
   Use it whenever the brand name appears mixed inside running text:
       <LexiqText>أنت تستخدم LexIQ لتحليل قضاياك</LexiqText> */
const EN_RE = /\bLex\s*IQ\b/gi;
const AR_RE = /ليكس\s*[آا]?ي\s*كيو/g;
export function LexiqText({ children, variant }) {
  if (typeof children !== "string") return children;
  const parts = [];
  let i = 0;
  const COMBINED = new RegExp(`${EN_RE.source}|${AR_RE.source}`, "g");
  let m;
  let key = 0;
  while ((m = COMBINED.exec(children)) !== null) {
    if (m.index > i) parts.push(children.slice(i, m.index));
    parts.push(
      <LexiqWordmark
        key={`lx-${key++}`}
        className="text-[1em] align-baseline"
        variant={variant}
      />,
    );
    i = m.index + m[0].length;
  }
  if (i < children.length) parts.push(children.slice(i));
  return parts.length ? <>{parts}</> : children;
}
